import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow } from "../components/Bits.jsx";

const MODULES = ["All", "Foundations", "M1 · Seeing (UI)", "M2 · Understanding (UX)", "M3 · Systems", "M4 · Access & Speed", "M5 · Build", "M6 · Practice"];
const EVIDENCE = {
  "primary-spec": { label: "Primary spec", note: "The standard itself — cite this, not summaries of it" },
  "research": { label: "Research", note: "Measured evidence: studies, surveys, large-scale data" },
  "practitioner-canon": { label: "Practitioner canon", note: "The field's tested, argued best practice" },
  "industry-reference": { label: "Industry reference", note: "Authoritative working references" },
  "tool": { label: "Tool", note: "Verification and production instruments" },
  "asset": { label: "Asset", note: "Licence-clean raw material" },
};

export default function Library() {
  const s = useSession();
  const [rows, setRows] = useState([]);
  const [mod, setMod] = useState(0);
  const [ev, setEv] = useState("all");
  const [qtext, setQtext] = useState("");
  const [add, setAdd] = useState(null); // senior add form
  const [msg, setMsg] = useState(null);

  useEffect(() => onSnapshot(collection(db, "library"), (snap) =>
    setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))), []);

  const list = useMemo(() => rows
    .filter((r) => mod === 0 || r.module === mod - 1 || (mod === 1 && r.module === 0))
    .filter((r) => ev === "all" || r.evidence === ev)
    .filter((r) => !qtext || (r.title + " " + r.why).toLowerCase().includes(qtext.toLowerCase()))
    .sort((a, b) => (a.evidence > b.evidence ? 1 : -1) || a.title.localeCompare(b.title)), [rows, mod, ev, qtext]);

  const submitAdd = async () => {
    if (!add.title?.trim() || !add.url?.trim() || !add.why?.trim()) return setMsg("Title, URL, and the one-line why are required — no entry lands here without a reason.");
    await addDoc(collection(db, "library"), {
      ...add, module: Number(add.module ?? 0), addedBy: s.user.uid, seededAt: new Date().toISOString(),
    });
    setAdd(null); setMsg("Added to the Library.");
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div className="row between wrapflex">
        <div>
          <Eyebrow>The Library</Eyebrow>
          <h1 style={{ fontSize: 24, marginTop: 4 }}>Curated, graded, and small on purpose</h1>
        </div>
        {s.isSenior && <button className="btn ghost" onClick={() => setAdd(add ? null : { evidence: "industry-reference", module: 0 })}>{add ? "Close" : "+ Add entry"}</button>}
      </div>
      <p style={{ color: "var(--graphite)", maxWidth: "70ch" }}>
        Every entry carries an evidence grade and one line on why it earned its place. The internet has
        everything; this shelf has what survives scrutiny. Reviewed every cohort in the revision window —
        if something here has gone stale, tell your Studio Master and it will face the review.
      </p>

      {s.isSenior && add && (
        <div className="card" style={{ margin: "12px 0" }}>
          <div className="row wrapflex" style={{ gap: 8 }}>
            <input type="text" placeholder="Title" style={{ width: 260 }} onChange={(e) => setAdd({ ...add, title: e.target.value })} />
            <input type="text" placeholder="https://…" style={{ width: 260 }} onChange={(e) => setAdd({ ...add, url: e.target.value })} />
            <select onChange={(e) => setAdd({ ...add, evidence: e.target.value })} defaultValue="industry-reference">
              {Object.entries(EVIDENCE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select onChange={(e) => setAdd({ ...add, module: e.target.value })} defaultValue={0}>
              {MODULES.slice(1).map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <input type="text" placeholder="Type (book/spec/tool/…)" style={{ width: 140 }} onChange={(e) => setAdd({ ...add, type: e.target.value })} />
          </div>
          <input type="text" placeholder="Why it earned its place — one line" style={{ marginTop: 8 }} onChange={(e) => setAdd({ ...add, why: e.target.value })} />
          <button className="btn" style={{ marginTop: 8 }} onClick={submitAdd}>Add</button>
        </div>
      )}
      {msg && <div className="card" style={{ margin: "10px 0" }}>{msg}</div>}

      <div className="row wrapflex" style={{ gap: 6, margin: "12px 0" }}>
        {MODULES.map((m, i) => (
          <button key={i} className={`btn ${mod === i ? "" : "ghost"}`} style={{ padding: "6px 10px", fontSize: 10 }} onClick={() => setMod(i)}>{m}</button>
        ))}
      </div>
      <div className="row wrapflex" style={{ gap: 6, marginBottom: 12 }}>
        <button className={`btn ${ev === "all" ? "" : "ghost"}`} style={{ padding: "6px 10px", fontSize: 10 }} onClick={() => setEv("all")}>All evidence</button>
        {Object.entries(EVIDENCE).map(([k, v]) => (
          <button key={k} className={`btn ${ev === k ? "" : "ghost"}`} style={{ padding: "6px 10px", fontSize: 10 }} title={v.note} onClick={() => setEv(k)}>{v.label}</button>
        ))}
        <input type="text" placeholder="Search…" value={qtext} style={{ width: 180 }} onChange={(e) => setQtext(e.target.value)} />
      </div>

      {list.map((r) => (
        <div key={r.id} className="crit">
          <div className="row wrapflex" style={{ gap: 8 }}>
            <a href={r.url} target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>{r.title}</a>
            <span className="pill">{EVIDENCE[r.evidence]?.label ?? r.evidence}</span>
            <span className="pill">{MODULES[(r.module ?? 0) + 1] ?? "Foundations"}</span>
            {r.type && <span className="pill">{r.type}</span>}
          </div>
          <p style={{ fontSize: 14, margin: "4px 0 0", color: "var(--graphite)" }}>{r.why}</p>
        </div>
      ))}
      {!list.length && <p style={{ color: "var(--graphite)", fontStyle: "italic", marginTop: 16 }}>Nothing matches — widen the filters, or the Library hasn't been seeded yet (re-run seed.mjs).</p>}
    </div>
  );
}
